import Vue from 'vue';
import Vuex from 'vuex';

import { buildReportModule } from './modules/report';
import { buildScenarioModule } from './modules/scenario';
import { buildStepperModule } from './modules/stepper';
import { buildBatteryModule } from './modules/steps/battery';
import dev from './modules/steps/dev';
import eco from './modules/steps/eco';
import grid from './modules/steps/grid';
import { buildLoadModule } from './modules/steps/load';
import { buildPVModule } from './modules/steps/pv';
import { buildSizingModule } from './modules/steps/sizing';
import wizard from './modules/steps/wizard';
import { buildUserModule } from './modules/user';
import project from './modules/project';
import cgu from './modules/cgu';
import { buildGeneratorModule } from '@/store/modules/steps/generator';
import { axiosWrapper } from '@/services/axiosWrapper';
import translator from '@/i18n';
import _t from '@/helpers/timeHelper';
import { download } from '@/helpers/download';
import { buildPresizingModule } from '@/store/modules/presizing';
import _rulez from '../helpers/rulesHelper';
import ruleService from '@/services/ruleService';
import _log from '@/helpers/log';
import { buildReferencesService } from '@/services/referencesService';
import stepperHelper from '@/helpers/rulesModules/stepperHelper';
import auth from '@/services/auth';
import { buildRuleModule } from '@/store/modules/rules';
import { rulesGenerator } from '@/helpers/rulesModules/scenarioRules';
import { buildSiteMapModule } from '@/store/modules/sitemap';
import { assignToPath, getPaths, getValueFromPath } from '@/helpers/pathHelper';
import localeService from '@/services/localeService';
import { buildOverlayModule } from '@/store/modules/overlay';
import { buildStructureModule } from '@/store/modules/structure';
import { buildReferencesModule } from '@/store/modules/references';
import { buildSensitivityAnalysisModule } from '@/store/modules/sensitivityAnalysis';
import { buildCostingToolModule } from '@/store/modules/costingTool';
import { buildRenFrPVModule } from './modules/steps/ren-fr/ren_fr_pv';
import { buildRenFrWindModule } from './modules/steps/ren-fr/ren_fr_wind';
import { buildRevenueModule } from './modules/steps/ren-fr/revenue';
import { buildCapexModule } from './modules/steps/ren-fr/capex';
import { buildOpexModule } from './modules/steps/ren-fr/opex';
import { buildStructureFinanceModule } from './modules/steps/ren-fr/structureFinance';

Vue.use(Vuex);

const referencesService = buildReferencesService({
  loadFile: axiosWrapper.getFromLocalJson,
  loadRules: ruleService.loadFieldsSpec
});

const scenario = buildScenarioModule({
  http: {
    get: axiosWrapper.getFromApi,
    post: axiosWrapper.postToApi,
    put: axiosWrapper.putOnApi
  },
  ruleHelper: _rulez,
  loadFile: axiosWrapper.getFromLocalJson,
  getPaths,
  assignToPath,
  refreshLocaleMessages: localeService.refreshLocaleMessages,
  i18n: translator,
  errorHandler: _log.logError
});

const load = buildLoadModule({
  post: axiosWrapper.postToApi,
  errorHandler: _log.logError,
  ruleHelper: _rulez
});

const generator = buildGeneratorModule({
  references: referencesService,
  ruleHelper: _rulez
});

const pv = buildPVModule({
  referencesService,
  post: axiosWrapper.postToApi,
  errorHandler: _log.logError
});

const stepper = buildStepperModule({
  steps: stepperHelper.allSteps,
  findRule: _rulez.getFromRulesDictionary
});

const user = buildUserModule({
  getFromApi: axiosWrapper.getFromApi,
  authenticationService: auth
});

const battery = buildBatteryModule({
  references: referencesService
});

const scenarioRules = buildRuleModule({
  loadFile: axiosWrapper.getFromLocalJson,
  assignSpecificationsToRules: ruleService.assignSpecificationsToRules,
  assignCountryReferences: ruleService.assignCountryReferences,
  rulesGenerator: () => rulesGenerator({ fileLoader: axiosWrapper.getFromLocalJson })
});

const report = buildReportModule({
  get: axiosWrapper.getWithConfigFromApi,
  download,
  formatDate: _t.formatDateForName
});

const siteMap = buildSiteMapModule({
  loadFile: axiosWrapper.getFromLocalJson,
  getPaths,
  assignToPath,
  getValueFromPath,
  refreshLocaleMessages: localeService.refreshLocaleMessages,
  i18n: translator,
  errorHandler: _log.logError
});

const preSizing = buildPresizingModule({ get: axiosWrapper.getFromApi });

const overlay = buildOverlayModule();

const sizing = buildSizingModule({
  http: {
    get: axiosWrapper.getFromApi,
    post: axiosWrapper.postToApi
  },
  assignSpecificationsToRules: ruleService.assignSpecificationsToRules,
  errorHandler: _log.logError
});

const structure = buildStructureModule({
  http: {
    get: axiosWrapper.getFromApi,
    put: axiosWrapper.putOnApi,
    post: axiosWrapper.postToApi
  },
  errorHandler: _log.logError
});

const references = buildReferencesModule({
  loadFile: axiosWrapper.getFromLocalJson,
  errorHandler: _log.logError,
  referencesService
});

const sensitivityAnalysis = buildSensitivityAnalysisModule({
  http: {
    get: axiosWrapper.getFromApi,
    post: axiosWrapper.postToApi
  },
  errorHandler: _log.logError
});
const costingTool = buildCostingToolModule({
  http: {
    get: axiosWrapper.getFromApi,
    post: axiosWrapper.postToApi
  },
  errorHandler: _log.logError
});

const ren_fr_pv = buildRenFrPVModule();
const ren_fr_wind = buildRenFrWindModule();
const revenue = buildRevenueModule();
const capex = buildCapexModule();
const opex = buildOpexModule();
const structure_finance = buildStructureFinanceModule();

export default new Vuex.Store({
  modules: {
    user,
    structure,
    stepper,
    battery,
    grid,
    pv,
    load,
    generator,
    sizing,
    dev,
    eco,
    wizard,
    scenario,
    report,
    project,
    cgu,
    preSizing,
    scenarioRules,
    siteMap,
    overlay,
    references,
    sensitivityAnalysis,
    costingTool,
    ren_fr_pv,
    ren_fr_wind,
    revenue,
    capex,
    opex,
    structure_finance
  }
});
